/**
 * Team Bios Management Page
 *
 * Main page for managing team member bios.
 * Allows Owner/Manager to configure settings and edit member bios.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import Header from '../../components/layout/Header';
import { YouTubeVideoButton } from '../../components/common';
import { useGetTeamBioConfigQuery } from '../../api/teamBiosApi';
import { useOwnerManagerAccess } from '../../hooks/useToolAccess';
import { useSessionAuth } from '../../hooks/useSessionAuth';
import StudioSelectorControl from '../../components/common/StudioSelectorControl';
import TeamBiosConfigPanel from './components/TeamBiosConfigPanel';
import NoToolAccess from '../../components/common/NoToolAccess';

const TeamBiosManagement: React.FC = () => {
  const navigate = useNavigate();

  // Get authentication state
  const { isAuthenticated, isLoading: authLoading } = useSessionAuth();

  // Get studio access information (Owner/Manager only)
  const {
    eligibleStudios,
    hasToolAccess,
    hasSingleStudio,
    isLoading: toolAccessLoading,
  } = useOwnerManagerAccess();

  // Component state
  const [selectedStudioId, setSelectedStudioId] = useState<string | null>(null);

  // Fetch team bio config (auto-creates if doesn't exist)
  const {
    data: config,
    isLoading: configLoading,
    error: configError,
  } = useGetTeamBioConfigQuery(selectedStudioId || '', {
    skip: !selectedStudioId,
  });

  // Handle studio selection and access control
  useEffect(() => {
    // Wait for authentication and tool access data to load
    if (authLoading || toolAccessLoading) {
      return;
    }

    // If user is not authenticated, redirect to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Auto-select if user has exactly one eligible studio
    if (hasSingleStudio) {
      const [studio] = eligibleStudios;
      setSelectedStudioId(studio.id);
    }
  }, [
    navigate,
    authLoading,
    isAuthenticated,
    toolAccessLoading,
    hasSingleStudio,
    eligibleStudios,
  ]);

  // Show no access message if user doesn't have required permissions
  if (!authLoading && !toolAccessLoading && !hasToolAccess) {
    return (
      <NoToolAccess toolName="Team Bios" requiredRoles={['owner', 'manager']} />
    );
  }

  if (configLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (configError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Failed to load team bios configuration. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            Team Bios Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure how your team members are displayed on the public team
            bios page
          </Typography>
        </Box>

        {/* Tutorial Video Section */}
        <Box
          sx={{
            mb: 4,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Watch our step-by-step tutorial to learn how to set up and customize
            your team bios page.
          </Typography>
          <YouTubeVideoButton
            videoId="I1paHUh7s4Q"
            videoTitle="Team Bios Tutorial"
            buttonText="Watch Tutorial"
            variant="outlined"
            size="small"
          />
        </Box>

        {/* Studio Selector - Only shows for multiple studios */}
        <Box mb={4}>
          <StudioSelectorControl
            studios={eligibleStudios}
            selectedStudioId={selectedStudioId}
            onStudioChange={setSelectedStudioId}
          />
        </Box>

        {/* Info Alert for Multiple Studios */}
        {eligibleStudios.length > 1 && !selectedStudioId && (
          <Box mb={4}>
            <Alert severity="info" sx={{ maxWidth: 400 }}>
              Please select a studio to manage its team bios.
            </Alert>
          </Box>
        )}

        {/* Content area - only show if studio is selected */}
        {selectedStudioId && (
          <Paper sx={{ p: 3 }}>
            <TeamBiosConfigPanel studioId={selectedStudioId} config={config!} />
          </Paper>
        )}
      </Container>
    </>
  );
};

export default TeamBiosManagement;
